import { useEffect, useState } from 'react';
import { apiFetch, useAuthStore } from '../stores/authStore';

/**
 * Shared subscription to GET /api/trader/rate-limit-status — the Phase 2 status
 * bar's account cluster: the API tier + 429 pressure for the account the caller
 * actually trades through (org pool or personal keys). One poll loop + cached
 * value shared across consumers, mirroring useHeadroom so we don't fire the
 * request once per mounted view.
 *
 * The pressure here is REACTIVE (derived from the relay's per-account 429
 * backoff) — a predictive token-spend gauge is the Phase 3 governor's job.
 */

// 1.5s, matching the bot tick. The relay serves this from memory (no Kalshi
// call per poll), so the dash→relay path has NO rate-limit cost — we can poll
// fast enough to catch a 1s backoff blip and feel live.
const POLL_MS = 1_500;

export interface RateLimitStatus {
  /** Which account the caller trades through, or null when no keys resolve. */
  scope: 'org' | 'user' | null;
  tier: 'basic' | 'advanced' | 'unknown';
  /** True while the shared account bucket is in 429 backoff right now. */
  throttled: boolean;
  /** Exponential-escalation depth — how hard/repeatedly we've hit the wall. */
  rateLimitStrikes: number;
  /** ms until the current backoff window clears (0 when not throttled). */
  msUntilClear: number;
  keyPoolMode?: string;
  keyCount: number;
  upgradedKeyCount: number;
  anyQuarantined: boolean;
  /** Relay's recent OUTBOUND request rate to this account (reads + writes/s) —
   *  drives the graduated fill. NOTE: this is OUR footprint only; external load
   *  on a shared account shows up as `throttled`, not here. */
  reqPerSec?: number;
  /** Per-second request budget for the account's tier (Basic ~20/s, Advanced
   *  ~30/s at ~10 tok/req) — the graduated meter's denominator. */
  budgetReqPerSec?: number;
}

let cached: RateLimitStatus | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<(s: RateLimitStatus | null) => void>();

async function fetchOnce(): Promise<void> {
  try {
    const res = await apiFetch('/api/trader/rate-limit-status');
    if (!res.ok) return;
    cached = (await res.json()) as RateLimitStatus;
    listeners.forEach((l) => l(cached));
  } catch {
    // silent — keep last-good so a blip doesn't flicker the bar
  }
}

function ensurePolling(): void {
  if (pollTimer) return;
  pollTimer = setInterval(fetchOnce, POLL_MS);
}

function stopPolling(): void {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
}

/** Force a fresh fetch (e.g. right after a manual tier upgrade). */
export function bumpRateLimitStatus(): void {
  fetchOnce();
}

/** Wipe the cache — used on logout so a different user doesn't see stale. */
export function resetRateLimitStatusCache(): void {
  cached = null;
  stopPolling();
  listeners.clear();
}

export function useRateLimitStatus(): RateLimitStatus | null {
  const isAuthed = useAuthStore((s) => s.status === 'authenticated');
  const [snap, setSnap] = useState<RateLimitStatus | null>(cached);

  useEffect(() => {
    if (!isAuthed) return;
    listeners.add(setSnap);
    if (listeners.size === 1) {
      ensurePolling();
      fetchOnce();
    } else if (cached) {
      setSnap(cached);
    }
    return () => {
      listeners.delete(setSnap);
      if (listeners.size === 0) stopPolling();
    };
  }, [isAuthed]);

  return snap;
}
